/**
 * Personnel Map with District Polygons
 *
 * Uses GeoJSON boundaries to show district areas colored by personnel adequacy level
 */

import { useEffect, useState } from 'react'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { Users, AlertTriangle, XCircle, Loader2 } from 'lucide-react'
import { usePersonnelDistricts } from '../../services/api/personnel'
import { MapErrorBoundary } from './MapErrorBoundary'
import { logger } from '@/shared/utils/logger'

interface DistrictPersonnel {
  id: string
  name_th: string
  name_en: string
  adequacy_level: 'adequate' | 'shortage' | 'critical'
  total_staff: number
  doctors: number
  nurses: number
  ratio_per_10k: number
}

interface PersonnelMapPolygonProps {
  className?: string
  year?: number
  selectedDistrictId?: number | null
  selectedDistrictName?: string | null
}

const getAdequacyColor = (level: 'adequate' | 'shortage' | 'critical', isHovered: boolean = false) => {
  const opacity = isHovered ? 0.85 : 0.65
  switch (level) {
    case 'adequate':
      return `rgba(34, 197, 94, ${opacity})` // Green 500 - เพียงพอ
    case 'shortage':
      return `rgba(249, 115, 22, ${opacity})` // Orange 500 - ขาดแคลน
    case 'critical':
      return `rgba(239, 68, 68, ${opacity})` // Red 500 - วิกฤต
  }
}

const getAdequacyLabel = (level: 'adequate' | 'shortage' | 'critical') => {
  switch (level) {
    case 'adequate': return 'เพียงพอ'
    case 'shortage': return 'ขาดแคลน'
    case 'critical': return 'วิกฤต'
  }
}

// Inner component that applies GeoJSON to the map
function MapDataLayer({
  districts,
  geoJsonData,
  selectedDistrictName,
}: {
  districts: DistrictPersonnel[]
  geoJsonData: any
  selectedDistrictName?: string | null
}) {
  const map = useMap()

  // Map GeoJSON district names to our data
  const getDistrictData = (districtNameEn: string): DistrictPersonnel | undefined => {
    const found = districts.find(d => d.name_en === districtNameEn)
    if (!found) {
      logger.warn(`⚠️ District not found: "${districtNameEn}" (GeoJSON) - Available:`, districts.map(d => d.name_en))
    }
    return found
  }

  // Apply GeoJSON to map
  useEffect(() => {
    if (!map || !geoJsonData) return

    // Debug: Log district counts
    logger.debug(`📊 Personnel Districts loaded: ${districts.length}`)
    logger.debug(`📊 GeoJSON features: ${geoJsonData.features?.length || 0}`)

    // Clear existing data
    map.data.forEach(feature => map.data.remove(feature))

    // Add GeoJSON
    map.data.addGeoJson(geoJsonData)

    // Style function (gray out non-selected districts when filtering)
    map.data.setStyle((feature) => {
      const districtName = String(feature.getProperty('NAME_2'))
      const districtData = getDistrictData(districtName)
      const isSelectedCheck = districtData?.name_th === selectedDistrictName
      const hasFilter = selectedDistrictName !== null && selectedDistrictName !== undefined

      if (!districtData) {
        return {
          fillColor: '#e2e8f0',
          fillOpacity: 0.3,
          strokeColor: '#94a3b8',
          strokeWeight: 1,
          strokeOpacity: 0.5,
        }
      }

      // When a district is selected, gray out all others
      if (hasFilter && !isSelectedCheck) {
        return {
          fillColor: '#d1d5db', // Gray color
          fillOpacity: 0.3,      // Very transparent
          strokeColor: '#9ca3af',
          strokeWeight: 0.5,
          strokeOpacity: 0.4,
        }
      }

      // Highlight selected district or show normal colors
      return {
        fillColor: getAdequacyColor(districtData.adequacy_level, isSelectedCheck),
        fillOpacity: isSelectedCheck ? 0.9 : 0.65,
        strokeColor: isSelectedCheck ? '#1e40af' : '#64748b',
        strokeWeight: isSelectedCheck ? 3 : 1,
        strokeOpacity: isSelectedCheck ? 1 : 0.5,
      }
    })

    // Hover events
    let infoWindow: google.maps.InfoWindow | null = null

    map.data.addListener('mouseover', (event: any) => {
      const districtName = event.feature.getProperty('NAME_2')
      const districtData = getDistrictData(districtName)

      if (districtData) {
        map.data.overrideStyle(event.feature, {
          strokeColor: '#1e3a8a',
          strokeWeight: 2,
          fillOpacity: 0.85,
        })

        // Show info window
        const geometry = event.feature.getGeometry()
        let position: google.maps.LatLng | null = null

        if (geometry.getType() === 'Polygon') {
          const polygon = geometry as google.maps.Data.Polygon
          const bounds = new google.maps.LatLngBounds()
          polygon.getArray()[0].getArray().forEach((latLng: google.maps.LatLng) => {
            bounds.extend(latLng)
          })
          position = bounds.getCenter()
        } else if (geometry.getType() === 'MultiPolygon') {
          const multiPolygon = geometry as google.maps.Data.MultiPolygon
          const bounds = new google.maps.LatLngBounds()
          multiPolygon.getArray().forEach(polygon => {
            polygon.getArray()[0].getArray().forEach((latLng: google.maps.LatLng) => {
              bounds.extend(latLng)
            })
          })
          position = bounds.getCenter()
        }

        if (position) {
          const adequacyIcon = districtData.adequacy_level === 'adequate'
            ? '✅'
            : districtData.adequacy_level === 'shortage'
            ? '⚠️'
            : '❌'

          infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; font-family: 'IBM Plex Sans Thai', sans-serif; min-width: 200px;">
                <div style="font-weight: 600; font-size: 14px; color: #1e3a5f; margin-bottom: 8px; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px;">
                  ${adequacyIcon} ${districtData.name_th}
                </div>
                <div style="font-size: 13px; line-height: 1.6; color: #475569;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span>สถานะ:</span>
                    <strong style="color: ${districtData.adequacy_level === 'adequate' ? '#22c55e' : districtData.adequacy_level === 'shortage' ? '#f97316' : '#ef4444'}">
                      ${getAdequacyLabel(districtData.adequacy_level)}
                    </strong>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span>บุคลากรทั้งหมด:</span>
                    <strong>${String(districtData.total_staff.toLocaleString('th-TH'))} คน</strong>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span>แพทย์:</span>
                    <strong>${String(districtData.doctors.toLocaleString('th-TH'))} คน</strong>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span>พยาบาล:</span>
                    <strong>${String(districtData.nurses.toLocaleString('th-TH'))} คน</strong>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-top: 1px solid #e2e8f0; padding-top: 4px; margin-top: 4px;">
                    <span>อัตราส่วน:</span>
                    <strong style="color: #2563eb;">${String(districtData.ratio_per_10k.toFixed(1))} ต่อ 10K</strong>
                  </div>
                </div>
              </div>
            `,
            position,
          })
          infoWindow.open(map)
        }
      }
    })

    map.data.addListener('mouseout', () => {
      map.data.revertStyle()
      if (infoWindow) {
        infoWindow.close()
        infoWindow = null
      }
    })

    return () => {
      map.data.forEach(feature => map.data.remove(feature))
      if (infoWindow) {
        infoWindow.close()
      }
    }
  }, [map, geoJsonData, districts, selectedDistrictName])

  // Auto-zoom to selected district
  useEffect(() => {
    if (!map || !geoJsonData || !selectedDistrictName) {
      // Reset to default view when no district selected
      if (map && !selectedDistrictName) {
        map.setCenter({ lat: 15.25, lng: 102.08 })
        map.setZoom(9)
      }
      return
    }

    // Find the feature for selected district
    map.data.forEach(feature => {
      const districtName = String(feature.getProperty('NAME_2'))
      const districtData = getDistrictData(districtName)

      if (districtData?.name_th === selectedDistrictName) {
        // Get bounds of this feature
        const bounds = new google.maps.LatLngBounds()
        const geometry = feature.getGeometry()

        if (geometry && geometry.getType() === 'Polygon') {
          const polygon = geometry as google.maps.Data.Polygon
          polygon.getArray()[0].getArray().forEach((latLng: google.maps.LatLng) => {
            bounds.extend(latLng)
          })
        } else if (geometry && geometry.getType() === 'MultiPolygon') {
          const multiPolygon = geometry as google.maps.Data.MultiPolygon
          multiPolygon.getArray().forEach(polygon => {
            polygon.getArray()[0].getArray().forEach((latLng: google.maps.LatLng) => {
              bounds.extend(latLng)
            })
          })
        }

        // Zoom to this district's bounds
        map.fitBounds(bounds)
        // Optional: add some padding
        const currentZoom = map.getZoom() || 10
        if (currentZoom > 11) {
          map.setZoom(11) // Don't zoom too close
        }
      }
    })
  }, [map, geoJsonData, selectedDistrictName, districts])

  return null
}

function PersonnelMapPolygonInner({
  className = '',
  year = 2567,
  selectedDistrictName,
}: PersonnelMapPolygonProps) {
  const [geoJsonData, setGeoJsonData] = useState<any>(null)

  // Fetch personnel data from API
  const { data: apiData, isLoading: isLoadingDistricts, error: apiError } = usePersonnelDistricts(year)

  // Load GeoJSON
  useEffect(() => {
    fetch('/korat-districts.geojson')
      .then(res => res.json())
      .then(data => setGeoJsonData(data))
      .catch(err => logger.error('Failed to load GeoJSON:', err))
  }, [])

  const mapApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

  if (!mapApiKey) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 font-semibold">ไม่พบ Google Maps API Key</p>
        <p className="text-sm text-red-600 mt-1">กรุณาตั้งค่า VITE_GOOGLE_MAPS_API_KEY ใน .env</p>
      </div>
    )
  }

  // Loading state
  if (isLoadingDistricts) {
    return (
      <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-lg p-12 text-center">
        <Loader2 className="w-12 h-12 text-korat-navy-500 mx-auto mb-4 animate-spin" />
        <p className="text-korat-navy-700 font-semibold">กำลังโหลดข้อมูลบุคลากร...</p>
      </div>
    )
  }

  // Error state
  if (apiError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 font-semibold">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
        <p className="text-sm text-red-600 mt-1">{apiError.message || 'กรุณาลองใหม่อีกครั้ง'}</p>
      </div>
    )
  }

  // Transform API data to match component interface
  const districts: DistrictPersonnel[] = (apiData?.districts || []).map(d => ({
    id: d.district_id.toString(),
    name_th: d.name_th,
    name_en: d.name_en,
    adequacy_level: d.adequacy_level,
    total_staff: d.total_staff,
    doctors: 0, // Not provided by API, could be calculated from by_category if needed
    nurses: 0, // Not provided by API
    ratio_per_10k: d.ratio_per_10k,
  }))

  // Calculate statistics
  const adequateCount = districts.filter(d => d.adequacy_level === 'adequate').length
  const shortageCount = districts.filter(d => d.adequacy_level === 'shortage').length
  const criticalCount = districts.filter(d => d.adequacy_level === 'critical').length
  const totalStaff = districts.reduce((sum, d) => sum + d.total_staff, 0)
  const avgRatio = districts.reduce((sum, d) => sum + d.ratio_per_10k, 0) / districts.length

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Map */}
      <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-lg overflow-hidden">
        <APIProvider apiKey={mapApiKey}>
          <GoogleMap
            mapId="personnel-district-map"
            defaultCenter={{ lat: 15.25, lng: 102.08 }}
            defaultZoom={9}
            gestureHandling="greedy"
            disableDefaultUI={false}
            style={{ width: '100%', height: '600px' }}
          >
            {geoJsonData && (
              <MapDataLayer
                districts={districts}
                geoJsonData={geoJsonData}
                selectedDistrictName={selectedDistrictName}
              />
            )}
          </GoogleMap>
        </APIProvider>
      </div>

      {/* Legend & Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Adequate */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full bg-green-500" />
            <span className="font-semibold text-green-800">เพียงพอ</span>
          </div>
          <div className="text-2xl font-bold text-green-700 tabular-nums">{adequateCount}</div>
          <div className="text-xs text-green-600 mt-1">อำเภอที่มีบุคลากรเพียงพอ</div>
          <div className="text-xs text-slate-600 mt-2">
            อัตราส่วน ≥ 9.5 ต่อ 10K คน
          </div>
        </div>

        {/* Shortage */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full bg-orange-500" />
            <span className="font-semibold text-orange-800">ขาดแคลน</span>
          </div>
          <div className="text-2xl font-bold text-orange-700 tabular-nums">{shortageCount}</div>
          <div className="text-xs text-orange-600 mt-1">อำเภอที่ขาดแคลนบุคลากร</div>
          <div className="text-xs text-slate-600 mt-2">
            อัตราส่วน 7.0-9.4 ต่อ 10K คน
          </div>
        </div>

        {/* Critical */}
        <div className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full bg-red-500" />
            <span className="font-semibold text-red-800">วิกฤต</span>
          </div>
          <div className="text-2xl font-bold text-red-700 tabular-nums">{criticalCount}</div>
          <div className="text-xs text-red-600 mt-1">อำเภอที่ขาดแคลนอย่างวิกฤต</div>
          <div className="text-xs text-slate-600 mt-2">
            อัตราส่วน &lt; 7.0 ต่อ 10K คน
          </div>
        </div>
      </div>

      {/* Summary Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 leading-relaxed">
            <strong>สรุป:</strong> จังหวัดนครราชสีมามีบุคลากรทางการแพทย์ทั้งหมด{' '}
            <strong className="tabular-nums">{totalStaff.toLocaleString('th-TH')}</strong> คน{' '}
            โดยมีอัตราส่วนเฉลี่ย <strong className="tabular-nums">{avgRatio.toFixed(1)}</strong> คนต่อประชากร 10,000 คน
            {' '}— พื้นที่ห่างไกลและเทือกเขาตอนล่างมักมีปัญหาขาดแคลนบุคลากรอย่างเฉียบพลัน
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Personnel Map with Error Boundary
 * Wraps PersonnelMapPolygonInner with MapErrorBoundary to handle map errors
 */
export function PersonnelMapPolygon(props: PersonnelMapPolygonProps) {
  return (
    <MapErrorBoundary>
      <PersonnelMapPolygonInner {...props} />
    </MapErrorBoundary>
  )
}
